import { constants, useBreakpointRange, useKeyUp } from "@newsela/angelou";
import { actionNames, objectTypes } from "@newsela/snitch";
import { useRouter } from "next/router";
import { useRef } from "react";
import FocusLock from "react-focus-lock";
import { FocusOn } from "react-focus-on";

import { ATTACHMENT_CONTENT_TYPE } from "@/types/models";
import { GenerationGeniusAPIResponse } from "@/types/models/GenerationGenius.interface";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import ActivityHeader from "@/view/ActivityHeader/ActivityHeader";
import {
  containerCss,
  focusContainerCss,
  wrapperCss,
} from "@/view/ActivityPanel/ActivityPanel.style";
import ActivityPanelTab from "@/view/ActivityPanel/ActivityPanelTab";
import { GeniusAttachmentResponse } from "@/view/GeniusContent/GeniusContent.interface";
import GeniusDiscussionContainer from "@/view/GeniusDiscussionContainer";
import GeniusTeacherResourcesContainer from "@/view/GeniusTeacherResourcesContainer/GeniusTeacherResourcesContainer";
import GeniusVocabularyContainer from "@/view/GeniusVocabularyContainer";
import PanelHeader from "@/view/PanelHeader";
import useActivityPanel from "@/view/utils/useActivityPanel";
import useAttachmentsContext from "@/view/utils/useAttachmentsContext";
import { TGeniusPanelName } from "./GeniusActivityPanel.constant";
import {
  activityListCss,
  panelCss,
  panelItemWrapperCss,
  sectionCss,
  sectionTextCss,
  sectionTitleCss,
} from "./GeniusActivityPanel.style";
import GeniusActivityPanelOption from "./GeniusActivityPanelOption";
import LearnMoreFormativeLink from "./LearnMoreFormativeLink";
import {
  getActivitiesFromContentData,
  getBackButtonHandler,
  getClickHandler,
} from "./utils";

export interface GeniusActivityPanelProps {
  contentData: GenerationGeniusAPIResponse;
}

const { zeroToXs, mdToLg, lgToInfinity } =
  constants.breakpoints.BreakpointRanges;

function getPanelContent(
  activityPanelKey: string | undefined,
  sharedContainerProps: any,
  renderActivityAndAttachmentList: () => React.ReactNode,
) {
  switch (activityPanelKey) {
    case "vocabulary":
      return <GeniusVocabularyContainer {...sharedContainerProps} />;
    case "discussion_questions":
      return <GeniusDiscussionContainer {...sharedContainerProps} />;
    case "teacher_resources":
      return <GeniusTeacherResourcesContainer {...sharedContainerProps} />;
    default:
      return renderActivityAndAttachmentList();
  }
}

const GeniusActivityPanel = ({ contentData }: GeniusActivityPanelProps) => {
  const router = useRouter();
  const shouldFocusCloseButtonRef = useRef(false);
  const { isTeacher, isStudent } = useGetUserProperties() || {};
  const onClickBackButton = getBackButtonHandler(router);

  const { isActivityPanelExpanded, setIsActivityPanelExpanded } =
    useActivityPanel();

  const breakpointRange = useBreakpointRange();
  const isSmallerScreen =
    breakpointRange !== mdToLg && breakpointRange !== lgToInfinity;
  const isSmallestScreen = breakpointRange === zeroToXs;
  const { attachments } = useAttachmentsContext() as GeniusAttachmentResponse;
  const filteredAttachments = attachments?.filter(
    ({ content_type }) => content_type === ATTACHMENT_CONTENT_TYPE.FORMATIVE,
  );

  const { query } = router;
  const { levelId = "" } = query;
  const activityPanel = query["activityPanel"] as TGeniusPanelName;

  const backButtonEventProperties = {
    objectType: objectTypes.content,
    actionName: actionNames.navigate,
    streamProperties: {
      content_type: "Article",
      description: `Navigate back to Activity Picker from Generation Genius ${activityPanel}`,
      content_id: typeof levelId === "string" ? levelId : levelId[0] || "",
    },
  };

  const closeButtonEventProperties = {
    objectType: objectTypes.content,
    actionName: actionNames.hide,
    streamProperties: {
      content_type: "Article",
      description: `Close Activity Picker from Generation Genius ${activityPanel}`,
      content_id: typeof levelId === "string" ? levelId : levelId[0] || "",
    },
  };

  const onClickCloseButton = () => {
    setIsActivityPanelExpanded(false);
  };

  const onEsc = () => {
    if (isSmallerScreen) {
      setIsActivityPanelExpanded(false);
    }
  };

  useKeyUp("escape", onEsc, {
    dependencyArray: [breakpointRange],
  });

  const availableActivities = getActivitiesFromContentData({
    contentData,
    isTeacher: Boolean(isTeacher),
  });
  const handleClick = getClickHandler({ router, contentData });

  const sharedContainerProps = {
    shouldFocusCloseButtonRef,
    contentData,
    onClickBackButton,
    backButtonEventProperties,
    closeButtonEventProperties,
    onClickCloseButton,
  };

  const renderActivitiesSection = () => {
    if (availableActivities.length === 0) {
      return <h2 css={sectionTitleCss}>No Lesson Materials Available</h2>;
    }
    return (
      <>
        <h2 css={sectionTitleCss}>Lesson Materials</h2>
        <ul role="group" css={activityListCss}>
          {availableActivities.map(({ activityPanel: panel, title, type }) => (
            <GeniusActivityPanelOption
              key={title}
              type={type}
              onClick={() => handleClick({ type, activityPanel: panel })}
            >
              {title}
            </GeniusActivityPanelOption>
          ))}
        </ul>
      </>
    );
  };
  const renderAttachmentsSection = () => {
    if (
      !isTeacher ||
      !filteredAttachments ||
      filteredAttachments.length === 0
    ) {
      return null;
    }
    return (
      <>
        <h2 css={sectionTitleCss}>Assessments</h2>
        <p css={sectionTextCss}>
          You can build and assign the following assessments in Formative.
        </p>
        <ul role="group" css={activityListCss}>
          {(filteredAttachments ?? []).map(({ data: { name, url } }) => (
            <GeniusActivityPanelOption
              key={name}
              type="external-link"
              onClick={() => handleClick({ type: "external-link", url })}
            >
              {name}
            </GeniusActivityPanelOption>
          ))}
        </ul>
        <LearnMoreFormativeLink />
      </>
    );
  };
  const renderActivityAndAttachmentList = () => (
    <section css={sectionCss}>
      <PanelHeader
        onClickCloseButton={onClickCloseButton}
        closeButtonEventProperties={closeButtonEventProperties}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
      >
        <ActivityHeader isStudent={isStudent ?? true} isArticle={false} />
      </PanelHeader>
      <div css={panelItemWrapperCss}> {renderActivitiesSection()}</div>
      <div css={panelItemWrapperCss}>{renderAttachmentsSection()}</div>
    </section>
  );

  const activityPanelContent = getPanelContent(
    activityPanel,
    sharedContainerProps,
    renderActivityAndAttachmentList,
  );

  const getContentsWithFocusLockComponent = (isPanelVisible: boolean) => {
    // FocusOn prevents screen readers from reading hidden elements within in the activities panel when view on mobile devices.
    if (isSmallestScreen) {
      // There is a bug in FocusOn that prevents click events from being registered
      // when the panel is hidden. So we wrap panel content in FocusOn only when
      // the panel is opened, and we actually need trap the focus within the panel
      return isPanelVisible ? (
        <FocusOn scrollLock={false} css={focusContainerCss}>
          {activityPanelContent}
        </FocusOn>
      ) : (
        activityPanelContent
      );
    }
    // FocusLock maintains focus in the panel, but allows scrolling on the page when small/medium screens are used.
    return (
      <FocusLock
        disabled={!isSmallerScreen}
        returnFocus
        css={focusContainerCss}
      >
        {activityPanelContent}
      </FocusLock>
    );
  };

  return (
    <div
      data-qa-selector="activity-panel"
      data-test-id="activity-panel"
      css={wrapperCss}
    >
      <div>
        <ActivityPanelTab
          isStudent={isStudent}
          onClick={() => {
            setIsActivityPanelExpanded(!isActivityPanelExpanded);
          }}
        />
      </div>
      <div css={panelCss(isActivityPanelExpanded)}>
        <div css={containerCss}>
          {getContentsWithFocusLockComponent(isActivityPanelExpanded)}
        </div>
      </div>
    </div>
  );
};

export default GeniusActivityPanel;
